import { consents } from '../constants';
import type { IEvaluatedConsent, IConsent } from '../types';
import type { IAddress } from '@/types';
import { useMemo } from 'react';

/**
 * Checks if a consent is required for a given customer's country.
 *
 * @param {Set<string>} requiredForCountries A set of country codes where this consent should be required
 * @param {IAddress['countryCode']} customerCountryCode The customer's country code
 * @returns {boolean} `true` if consent is required for all countries (`'*'`) or for the given country, otherwise `false`
 */
const isConsentRequired = (
  requiredForCountries: IConsent['requiredForCountries'],
  customerCountryCode: IAddress['countryCode']
): boolean => requiredForCountries.has('*') || requiredForCountries.has(customerCountryCode);

/**
 * Determines if consent is included based on the list of countries and the customer's country code.
 *
 * @param {Set<string>} includedForCountries A set of country codes where this consent should be included
 * @param customerCountryCode The customer's country code
 * @returns `true` if consent is included for all countries (`'*'`) or for the given country, otherwise `false`
 */
const isConsentIncluded = (
  includedForCountries: IConsent['includedForCountries'],
  customerCountryCode: IAddress['countryCode']
): boolean => includedForCountries.has('*') || includedForCountries.has(customerCountryCode);

interface IUseQuoteConsents {
  areConsentsEvaluated: boolean;
  consents: IEvaluatedConsent[];
}

export const useQuoteConsents = (
  customerCountryCode: IAddress['countryCode'] | undefined
): IUseQuoteConsents =>
  useMemo(() => {
    if (!customerCountryCode) {
      return { consents: [], areConsentsEvaluated: false };
    }

    const includedConsents = consents.filter(({ includedForCountries }) =>
      isConsentIncluded(includedForCountries, customerCountryCode)
    );

    return {
      consents: includedConsents.map(({ href, id, label, requiredForCountries }) => ({
        href,
        id,
        label,
        isRequired: isConsentRequired(requiredForCountries, customerCountryCode),
      })),
      areConsentsEvaluated: true,
    };
  }, [customerCountryCode]);
